import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import Promise from 'bluebird';
import { generateRandomString } from 'shared/utils';
import classnames from 'classnames';
import FormField from 'scy/FormField';
import FacebookIcon from 'images/f_logo_512.png';
import styles from './styles.mod.scss';
import { TertiaryButton, LoadingButton } from 'scy/buttons';
import { H2, H4, Small, Body } from 'scy/text';
import { secondary, accent, tertiary } from 'scy/text/styles.mod.scss';
import { Warning, DangerWarning } from 'scy/Warning';
import { flex, flex1, centerItems, centerContent } from 'scy/flex.mod.scss';
import { textCenter, width100, width50 } from 'scy/utils.mod.scss';
import {
  thirdPartyAccountLink,
  forcePasswordResetToken,
  accountLinkPasswordReset
} from 'shared/models/api';
import { trackNow } from 'shared/models/analytic';
import SauceyWordmark from 'images/icons/logos/wordmark.svg?react-svg';
import EmjayLogo from 'images/emjay/emjay-logo-wordmark.svg?react-svg';
import padding from 'scy/padding.mod.scss';
import margin from 'scy/margin.mod.scss';
import InputPin from 'scy/InputPin';

const sauceyOrange = `#fa6446`;
const PHONE_NUMBER_EXISTS_ERROR_CODE = 42001;
const TOKEN = generateRandomString(25);

export default function AccountLinkForm({
  initUser,
  onSuccess,
  userAccountType,
  toggleErrorModal,
  setErrorMessage
}) {
  const [errors, setErrors] = useState({});
  const [asyncError, setAsyncError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState(initUser ? initUser.email : '');
  const [password, setPassword] = useState('');
  const [confirmPW, setConfirmPW] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetPassword, setResetPassword] = useState(false);

  const userPhoneNumber = initUser && initUser.mobile;
  let isFBUser = initUser.authData && initUser.authData.facebook;
  let signInTitle = isFBUser
    ? 'Create your password'
    : resetPassword
    ? 'Reset your password'
    : 'Sign In With Saucey';
  let signInMessage = isFBUser
    ? 'Looks like you created your Saucey account using Facebook, however Emjay accounts require the creation of a new password, because... rules'
    : resetPassword
    ? 'This will be your password for both Saucey & Emjay'
    : `Enter your Saucey password to link<br />your Saucey & Emjay accounts`;

  let buttonText =
    isFBUser || resetPassword ? 'link account' : 'confirm password';

  function validateEmail(email) {
    if (!email) return "Email can't be blank";
  }

  function validatePassword(password) {
    if (!password) {
      setLoading(true);
      return "Password can't be blank";
    }
    setLoading(false);
  }

  function validatePasswordMatch(value) {
    if (!value) return "Password can't be blank";
    if (value !== password) {
      setLoading(true);
      return 'Passwords do not match';
    }
    setLoading(false);
  }

  function validateFields() {
    let errors = {};
    errors.email = validateEmail(email) || null;
    errors.password =
      validatePassword(password) || validatePasswordMatch(password);

    if (isFBUser || resetPassword) {
      errors.confirmPW = validatePasswordMatch(confirmPW) || null;
    }

    setErrors(errors);
    if (_.some(errors)) return Promise.reject(new Error());
    return Promise.resolve();
  }

  function handlePasswordReset() {
    return validateFields()
      .then(() => accountLinkPasswordReset(TOKEN, email, password))
      .then(res => {
        if (!res) return;
        if (res.error) {
          setAsyncError(res.error);
          return Promise.reject(new Error(res.error));
        }
      });
  }

  function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    setAsyncError(null);

    let updatePWPromise = Promise.resolve();
    if (resetPassword) {
      updatePWPromise = handlePasswordReset();
      updatePWPromise.catch(err => {
        if (err) {
          return;
        }
      });
    }
    return updatePWPromise
      .then(() => validateFields())
      .then(() => thirdPartyAccountLink({ email, password }))
      .then(data => {
        const response_error_code = data.code || null;
        if (response_error_code === PHONE_NUMBER_EXISTS_ERROR_CODE) {
          setErrorMessage(data.message);
          trackNow('Account Link Failed', { error: data });
          toggleErrorModal();
        } else if (!data.emjay_user_id || !data.redirect_url) {
          console.dir('account link error:', JSON.stringify(data)); // eslint-disable-line no-console
          trackNow('Account Link Failed', { error: data });
          setLoading(false);

          let errorMessage = data.error;
          if (!errorMessage) {
            errorMessage =
              'An unknown error occurred, please contact support@saucey.com';
          }
          return setAsyncError(errorMessage);
        } else if (onSuccess) {
          trackNow('Account Link Success', {
            data: data,
            account_type: userAccountType
          });
          onSuccess(data);
        }
      })
      .catch(({ message }) => {
        setLoading(false);
        setAsyncError(message);
      });
  }

  return (
    <>
      {showForgotPassword && (
        <ForgotPassword
          userPhoneNumber={userPhoneNumber || ''}
          setShowForgotPassword={setShowForgotPassword}
          setResetPassword={setResetPassword}
          email={email}
        />
      )}
      <form onSubmit={handleSubmit} className={margin.top_xs}>
        {!!asyncError && <Warning>{asyncError}</Warning>}
        <H4 tag="h3" className={textCenter}>
          <big>{signInTitle}</big>
        </H4>
        {isFBUser && (
          <div className={classnames(flex, centerContent, margin.top_sm)}>
            <img className={styles.fbIcon} src={FacebookIcon} />
          </div>
        )}
        <Small
          tag="h3"
          className={classnames(
            textCenter,
            secondary,
            padding.top_sm,
            padding.left_md,
            padding.right_md,
            padding.bottom_sm
          )}
          dangerouslySetInnerHTML={{ __html: signInMessage }}
        />
        {isFBUser && (
          <FormField
            id="user-email"
            name="email"
            labelText="Email"
            inputType="email"
            initialValue={email}
            onChange={setEmail}
            validate={validateEmail}
            error={errors.email}
          />
        )}

        {!resetPassword && (
          <FormField
            id="user-password"
            name="password"
            labelText="Password"
            inputType="password"
            onChange={setPassword}
            validate={validatePassword}
            error={errors.password}
          />
        )}

        {!isFBUser && !resetPassword && (
          <div
            className={styles.forgotPassword}
            onClick={() => setShowForgotPassword(!showForgotPassword)}
          >
            {
              <Body className={tertiary}>
                <small>Forgot Password?</small>
              </Body>
            }
          </div>
        )}
        {resetPassword && (
          <>
            <FormField
              labelText="New Password"
              name="password"
              inputType="password"
              onChange={setPassword}
              validatePassword={validatePassword}
            />
            <FormField
              labelText="Confirm New Password"
              name="password"
              inputType="password"
              onChange={setConfirmPW}
              error={errors.password}
            />
          </>
        )}

        {isFBUser && (
          <FormField
            id="user-password-confirm"
            name="password-confirm"
            labelText="Confirm Password"
            inputType="password"
            onChange={setConfirmPW}
            validate={validatePasswordMatch}
            error={errors.password}
          />
        )}
        <Small
          tag="h3"
          className={classnames(secondary, padding.bottom_sm, padding.top_sm)}
        >
          By continuing I hereby attest that I am 21 years or older, consent to
          Emjay&apos;s Term of Service & Privacy Policy, and to use electronic
          records and signatures, and opt-in to receive communications from
          Emjay
        </Small>
        <LoadingButton
          Button={TertiaryButton}
          type="submit"
          className={classnames(flex1, width100, margin.top_xs)}
          disabled={loading}
          loading={loading}
        >
          {buttonText}
        </LoadingButton>
      </form>
      <div
        className={classnames(
          styles.logos,
          flex,
          flex1,
          centerItems,
          centerContent
        )}
      >
        <SauceyWordmark color={sauceyOrange} height={40} width={100} />
        <div style={{ fontSize: '14px', margin: '0 2px 0 6px' }}>&#8553;</div>
        <EmjayLogo
          color="#2bb0a7"
          height={40}
          width={100}
          style={{ transform: 'translateY(8px)' }}
        />
      </div>
    </>
  );
}

const ForgotPassword = ({
  userPhoneNumber,
  setResetPassword,
  setShowForgotPassword,
  email
}) => {
  let lastTwoDigits =
    userPhoneNumber &&
    userPhoneNumber
      .split('')
      .reverse()
      .slice(0, 2)
      .reverse()
      .join('');

  const [error, setError] = useState('');
  useEffect(() => {}, []);
  return (
    <div className={styles.forgotPasswordModal}>
      {error ? (
        <DangerWarning className={classnames(width50, margin.top_lg)}>
          Please enter a valid PIN
        </DangerWarning>
      ) : null}
      <H2 className={!error && margin.top_lg}>Confirm Your Identity</H2>
      <Body
        className={classnames(
          textCenter,
          padding.right_md,
          padding.left_md,
          margin.top_xs
        )}
      >
        Enter PIN sent to your mobile number ending in xx
        {lastTwoDigits} to verify your identity
      </Body>
      <InputPin
        forcePasswordResetToken={forcePasswordResetToken}
        token={TOKEN}
        setError={setError}
        setResetPassword={setResetPassword}
        setShowForgotPassword={setShowForgotPassword}
        email={email}
      />
      <Body className={classnames(accent, margin.top_sm)}>
        <a href={`/forgot-password`}>Reset password via email instead</a>
      </Body>
    </div>
  );
};
